"use client";

import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

import { getAppVersion, track } from "@/lib/analytics";
import { submitFeedback } from "@/lib/api/api-client";
import { useAuth } from "@/contexts/auth-context";
import { usePlaybackState } from "@/contexts/studio-context";
import { useFeedbackPrompt } from "@/hooks/journey/use-feedback-prompt";
import {
  occupiedColumnWidth,
  useShellOccupancy,
} from "@/components/learn-piece/shell/shell-occupancy";
import type { JourneySnapshot } from "@/lib/learning-journey/journey";

import {
  MAX_MESSAGE,
  MIN_MESSAGE,
  PROMPT_COLORS,
  PROMPT_THANKS_MS,
} from "./feedback-prompt-palette";

/**
 * « Une chose à changer dans ce que tu viens de faire ? »
 *
 * # Pourquoi cette question
 *
 * Elle est singulière (« une chose »), rétrospective, et bornée à ce qui vient
 * de se passer plutôt qu'à Virtuosa en général. Elle demande une action, pas
 * un jugement : elle produit une phrase qu'on peut mettre dans un ticket.
 *
 * « Comment s'est passée cette session ? » rend « bien » sept fois sur dix. Une
 * note de 1 à 5 ne dit jamais quoi corriger, et à l'échelle d'une bêta sa
 * moyenne n'a aucun pouvoir statistique — seulement un grand pouvoir de
 * distraction. « Qu'est-ce qui t'a gêné ? » présuppose la gêne et laisse
 * échapper les « il manque X », qui sont la moitié des retours utiles.
 *
 * # Pourquoi elle n'ouvre pas la modale existante
 *
 * `FeedbackPanel` reste, et sert l'autre besoin : celui de quelqu'un qui vient
 * *déposer* quelque chose, quand il veut, avec un type à choisir. Ici c'est le
 * produit qui demande, une fois, sur un sujet précis. Livrer un formulaire à
 * trois champs à quelqu'un qui a cliqué sur une question, ce serait changer le
 * contrat après signature.
 *
 * # Où elle se pose
 *
 * En bas du roll, **sous la playhead** : ce qui est en dessous est déjà joué.
 * Le conteneur parent s'arrête à `pianoHeight` et `minimapWidth`, dont elle
 * hérite sans recalcul.
 *
 * Horizontalement, elle se centre sur la bande *libre* et non sur le conteneur.
 * La première version faisait l'inverse, sur l'idée que le panneau d'étape était
 * ancré en haut et laissait le bas dégagé — c'est faux en variante colonne, où
 * il prend 400 px sur toute la hauteur, et la capsule mordait dessus de 50 px.
 * Voir `shell-occupancy`.
 */

type Phase = "capsule" | "expanded" | "sending" | "thanks";

export type PracticeFeedbackPromptProps = {
  readonly snapshot: JourneySnapshot | null;
};

export function PracticeFeedbackPrompt({ snapshot }: PracticeFeedbackPromptProps) {
  // Lus ici plutôt que reçus en props : le seul site de montage n'a aucune
  // raison de connaître le contexte que le formulaire joint à son envoi.
  const pathname = usePathname();
  const appVersion = getAppVersion();

  const { visible, trigger, dismiss, markAnswered, close } = useFeedbackPrompt({ snapshot });
  const { user } = useAuth();
  const playing = usePlaybackState();

  const [phase, setPhase] = useState<Phase>("capsule");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  /**
   * `null` tant que personne n'a touché au champ : la valeur affichée est alors
   * celle du compte. Dérivé plutôt que recopié dans un effet, parce que
   * l'authentification se résout après le premier rendu — un effet de
   * synchronisation écraserait une saisie en cours le jour où le jeton arrive
   * en retard.
   */
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const email = emailDraft ?? user?.email ?? "";

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const tooShort = message.trim().length < MIN_MESSAGE;
  const expanded = phase !== "capsule";

  /**
   * La bande où se poser : le conteneur moins la colonne du panneau d'étape.
   *
   * Se centrer sur le conteneur entier semblait juste et ne l'était pas — en
   * variante colonne le panneau prend 400 px sur le bord droit, sur toute la
   * hauteur, et la capsule mordait dessus de 50 px. La forme change d'une
   * étape à l'autre, donc aucune constante ne peut la couvrir : on lit la
   * colonne réellement occupée. Une bande ou une capsule centrée n'en
   * occupent aucune, et s'en écarter latéralement décentrerait l'invitation
   * pour rien.
   *
   * En `calc` plutôt qu'en mesure : la géométrie est connue en CSS, et
   * observer le conteneur ferait dépendre la position d'un rendu de plus.
   */
  const blocked = occupiedColumnWidth(useShellOccupancy());
  const freeBand = `(100% - ${blocked}px)`;
  const centerOfFreeBand = `calc(${freeBand} / 2)`;

  // Le champ prend le focus au dépliement. C'est un mouvement provoqué par un
  // clic de l'utilisateur, pas un vol : à l'apparition, au contraire, rien ne
  // touche au focus — arracher le curseur à quelqu'un qui vient de valider une
  // phrase serait exactement l'interruption qu'on refuse.
  useEffect(() => {
    if (phase === "expanded") {
      textareaRef.current?.focus();
    }
  }, [phase]);

  // La lecture repart : la personne a répondu avec ses mains. Une capsule
  // posée sur le roll pendant une passe est la corvée qu'on voulait éviter.
  // Si la carte est dépliée et qu'elle contient du texte, on ne touche à
  // rien : détruire un brouillon serait pire que tout.
  useEffect(() => {
    if (playing && visible && phase === "capsule") {
      dismiss("resumed", false);
    }
  }, [playing, visible, phase, dismiss]);

  const handleSubmit = useCallback(async () => {
    if (tooShort || phase === "sending") {
      return;
    }

    setPhase("sending");
    setError(null);

    const trimmedMessage = message.trim();
    const trimmedEmail = email.trim();

    try {
      await submitFeedback({
        type: "session",
        message: trimmedMessage,
        email: trimmedEmail === "" ? null : trimmedEmail,
        pathname,
        appVersion,
      });

      markAnswered();
      setPhase("thanks");

      track({
        name: "feedback_prompt_submitted",
        properties: {
          trigger,
          length: trimmedMessage.length,
          hasEmail: trimmedEmail !== "",
        },
      });
    } catch {
      setPhase("expanded");
      setError("L'envoi a échoué. Réessaie, ou écris-nous à contact@virtuosa.io.");
    }
  }, [tooShort, phase, message, email, pathname, appVersion, markAnswered, trigger]);

  // Le remerciement s'efface tout seul : il n'accuse que réception, le rater ne
  // coûte rien. C'est l'inverse de l'invitation, qui demande quelque chose et
  // reste donc jusqu'à ce que la personne agisse.
  useEffect(() => {
    if (phase !== "thanks") {
      return;
    }

    const timer = setTimeout(close, PROMPT_THANKS_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [phase, close]);

  const handleOpen = useCallback(() => {
    setPhase("expanded");
    track({ name: "feedback_prompt_opened", properties: { trigger } });
  }, [trigger]);

  const handleClose = useCallback(() => {
    dismiss("closed", message.trim() !== "");
  }, [dismiss, message]);

  // Écouteur **local**, jamais sur `window` : un gestionnaire global volerait
  // Échap aux modales et au shell. C'est la règle que `ToastBanner` énonce
  // déjà pour la même raison.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape" && phase !== "thanks") {
        event.stopPropagation();
        handleClose();
      }
    },
    [phase, handleClose],
  );

  if (!visible) {
    return null;
  }

  return (
    <section
      role="region"
      aria-label="Invitation à donner un retour"
      onKeyDown={handleKeyDown}
      className="pointer-events-auto absolute bottom-3 -translate-x-1/2 z-40 feedback-prompt-enter"
      style={{
        left: centerOfFreeBand,
        width: expanded
          ? `min(380px, calc(${freeBand} - 32px))`
          : `min(480px, calc(${freeBand} - 32px))`,
        backgroundColor: PROMPT_COLORS.surface,
        backdropFilter: "blur(var(--blur-shell)) saturate(140%)",
        WebkitBackdropFilter: "blur(var(--blur-shell)) saturate(140%)",
        border: `1px solid ${PROMPT_COLORS.border}`,
        borderRadius: "var(--radius-tile)",
        boxShadow: "var(--shadow-popin)",
      }}
    >
      {phase === "thanks" ? (
        <p
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 px-4 py-2.5 text-[13px]"
          style={{ color: PROMPT_COLORS.text }}
        >
          <span
            aria-hidden="true"
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: PROMPT_COLORS.ok }}
          />
          Reçu. Merci.
        </p>
      ) : expanded ? (
        <ExpandedCard
          message={message}
          onMessageChange={setMessage}
          email={email}
          onEmailChange={setEmailDraft}
          error={error}
          sending={phase === "sending"}
          tooShort={tooShort}
          onSubmit={handleSubmit}
          onClose={handleClose}
          textareaRef={textareaRef}
        />
      ) : (
        <Capsule onOpen={handleOpen} onClose={handleClose} />
      )}
    </section>
  );
}

/**
 * La forme repliée. Une ligne, deux boutons, et la question déjà lisible —
 * il ne faut pas cliquer pour savoir ce qui est demandé.
 */
export function Capsule({ onOpen, onClose }: { onOpen: () => void; onClose: () => void }) {
  return (
    <div className="flex items-center gap-3 py-2.5 pl-4 pr-2.5">
      {/* L'annonce est `polite` : elle doit attendre que le lecteur d'écran ait
          fini ce qu'il disait, pas couper la parole. */}
      <p
        aria-live="polite"
        aria-atomic="true"
        className="flex-1 text-[13px] leading-[18px]"
        style={{ color: PROMPT_COLORS.text }}
      >
        Une chose à changer dans ce que tu viens de faire&nbsp;?
      </p>

      <button
        type="button"
        onClick={onOpen}
        aria-expanded={false}
        className="h-[30px] shrink-0 cursor-pointer rounded-lg px-3 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        style={{ color: PROMPT_COLORS.text, border: `1px solid ${PROMPT_COLORS.rule}` }}
      >
        Répondre
      </button>

      <CloseButton onClose={onClose} />
    </div>
  );
}

export function ExpandedCard({
  message,
  onMessageChange,
  email,
  onEmailChange,
  error,
  sending,
  tooShort,
  onSubmit,
  onClose,
  textareaRef,
}: {
  message: string;
  onMessageChange: (value: string) => void;
  email: string;
  onEmailChange: (value: string) => void;
  error: string | null;
  sending: boolean;
  tooShort: boolean;
  onSubmit: () => void;
  onClose: () => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
  // Le champ email n'apparaît qu'une fois la rédaction commencée. Au moment où
  // la personne décide de répondre, elle voit une question et une zone de
  // texte — un seul champ, comme promis. L'email arrive quand il y a quelque
  // chose à quoi répondre.
  const showEmail = message.trim().length > 10;

  return (
    <div className="flex flex-col gap-3 px-4 py-4">
      <div className="flex items-start gap-3">
        <label
          htmlFor="practice-feedback-message"
          className="flex-1 text-[13px] font-medium leading-[18px]"
          style={{ color: PROMPT_COLORS.text }}
        >
          Si tu pouvais changer une chose à ce que tu viens de faire, ce serait quoi&nbsp;?
        </label>
        <CloseButton onClose={onClose} />
      </div>

      <textarea
        id="practice-feedback-message"
        ref={textareaRef}
        value={message}
        onChange={(event) => onMessageChange(event.target.value.slice(0, MAX_MESSAGE))}
        placeholder="Ce qui t'a ralenti, ce que tu n'as pas trouvé, ce qui t'a agacé."
        disabled={sending}
        rows={3}
        className="resize-none rounded-lg px-3 py-2.5 text-[13px] leading-relaxed outline-none transition-colors duration-150 disabled:opacity-60"
        style={{
          color: PROMPT_COLORS.text,
          backgroundColor: "rgba(255,255,255,0.03)",
          border: `1px solid ${PROMPT_COLORS.rule}`,
        }}
      />

      {showEmail && (
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="practice-feedback-email"
            className="text-[11px]"
            style={{ color: PROMPT_COLORS.textMuted }}
          >
            Ton email, si tu veux une réponse.
          </label>
          <input
            id="practice-feedback-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            placeholder="ton@email.com"
            disabled={sending}
            className="h-9 rounded-lg px-3 text-[13px] outline-none transition-colors duration-150 disabled:opacity-60"
            style={{
              color: PROMPT_COLORS.text,
              backgroundColor: "rgba(255,255,255,0.03)",
              border: `1px solid ${PROMPT_COLORS.rule}`,
            }}
          />
        </div>
      )}

      {error !== null && (
        <p role="alert" className="text-[11px] leading-[16px]" style={{ color: PROMPT_COLORS.error }}>
          {error}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px]" style={{ color: PROMPT_COLORS.textMuted }}>
          {message.length}/{MAX_MESSAGE}
        </span>

        <button
          type="button"
          onClick={onSubmit}
          disabled={tooShort || sending}
          className="h-[34px] cursor-pointer rounded-lg px-4 text-[13px] font-semibold text-white transition-opacity duration-150 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          style={{ backgroundColor: PROMPT_COLORS.accent }}
        >
          {sending ? "Envoi…" : "Envoyer"}
        </button>
      </div>

      <p className="text-[11px] leading-[16px]" style={{ color: PROMPT_COLORS.textMuted }}>
        La page et la version sont jointes.
      </p>
    </div>
  );
}

export function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Fermer, ne plus proposer"
      className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-[15px] leading-none transition-colors duration-150 cursor-pointer hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      style={{ color: PROMPT_COLORS.textMuted }}
    >
      ×
    </button>
  );
}

/* L'animation d'entrée vit dans `globals.css` — `.feedback-prompt-enter`. */
